import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';

const INPUT_BOX_W = 160;
const INPUT_BOX_H = 44;
const MAX_INPUT_BOXES = 2;
const BRUSH_RADIUS = 6;

const ListenPart5 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal, zoomLevel = 1, sharedColor, setSharedColor }) => {
  const [coloredZones, setColoredZones] = useState({});
  // inputBoxes: [{ id, x, y, typedValue, zoneName }]  — max MAX_INPUT_BOXES items
  const [inputBoxes, setInputBoxes] = useState([]);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const imageContainerRef = useRef(null);
  const canvasRef = useRef(null);
  const isPaintingRef = useRef(false);
  const lastPointRef = useRef(null);
  // paintStrokes: [{ color, points: [{x,y}] }] — for redrawing on resize
  const [paintStrokes, setPaintStrokes] = useState([]);

  const colorQuestions = (exerciseData?.questions || []).filter(q => !q.question_text?.toLowerCase().startsWith('input'));
  const inputQuestions = (exerciseData?.questions || []).filter(q => q.question_text?.toLowerCase().startsWith('input'));

  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

  const getZones = () => exerciseData?.content_data?.zones || [];

  const handleImageLoad = () => setIsImageLoaded(true);

  const getScale = () => {
    const imageEl = imageContainerRef.current?.querySelector('img');
    if (!imageEl) return { scaleX: 1, scaleY: 1 };
    return {
      scaleX: imageEl.naturalWidth / imageEl.width,
      scaleY: imageEl.naturalHeight / imageEl.height,
    };
  };

  const findZoneForPoint = (x, y) => {
    const zones = getZones();
    const { scaleX, scaleY } = getScale();
    for (const zone of zones) {
      if (
        x >= zone.rect.x1 / scaleX &&
        x <= zone.rect.x2 / scaleX &&
        y >= zone.rect.y1 / scaleY &&
        y <= zone.rect.y2 / scaleY
      ) {
        return zone;
      }
    }
    return null;
  };

  // --- Canvas paint helpers ---

  const getCanvasXY = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / zoomLevel,
      y: (e.clientY - rect.top) / zoomLevel,
    };
  };

  const drawStroke = useCallback((ctx, points, color) => {
    if (points.length < 1) return;
    ctx.strokeStyle = color.toLowerCase();
    ctx.lineWidth = BRUSH_RADIUS * 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalAlpha = 0.45;
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    if (points.length === 1) {
      // Single dot
      ctx.lineTo(points[0].x + 0.5, points[0].y + 0.5);
    }
    ctx.stroke();
    ctx.globalAlpha = 1;
  }, []);

  const redrawAllStrokes = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    paintStrokes.forEach(s => drawStroke(ctx, s.points, s.color));
  }, [paintStrokes, drawStroke]);

  // Resize canvas to match image
  const syncCanvasSize = useCallback(() => {
    const canvas = canvasRef.current;
    const imageEl = imageContainerRef.current?.querySelector('img');
    if (!canvas || !imageEl) return;
    if (canvas.width !== imageEl.width || canvas.height !== imageEl.height) {
      canvas.width = imageEl.width;
      canvas.height = imageEl.height;
      redrawAllStrokes();
    }
  }, [redrawAllStrokes]);

  useEffect(() => {
    if (isImageLoaded) syncCanvasSize();
  }, [isImageLoaded, syncCanvasSize]);

  useEffect(() => {
    window.addEventListener('resize', syncCanvasSize);
    return () => window.removeEventListener('resize', syncCanvasSize);
  }, [syncCanvasSize]);

  // Paint handlers
  const handlePaintStart = (e) => {
    if (isReviewMode || !sharedColor || !isHydrated) return;
    // Don't paint if clicking on input boxes
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON') return;
    e.preventDefault();
    const pt = getCanvasXY(e);
    if (!pt) return;
    isPaintingRef.current = true;
    lastPointRef.current = pt;

    // Start new stroke
    setPaintStrokes(prev => [...prev, { color: sharedColor, points: [pt] }]);

    // Draw dot
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      drawStroke(ctx, [pt], sharedColor);
    }

    // Check zone intersection
    const zone = findZoneForPoint(pt.x, pt.y);
    if (zone) {
      setColoredZones(prev => ({ ...prev, [zone.name]: sharedColor }));
    }
  };

  const handlePaintMove = (e) => {
    if (!isPaintingRef.current || !sharedColor) return;
    e.preventDefault();
    const pt = getCanvasXY(e);
    if (!pt) return;

    // Add point to current stroke
    setPaintStrokes(prev => {
      const copy = [...prev];
      const last = copy[copy.length - 1];
      if (last) last.points = [...last.points, pt];
      return copy;
    });

    // Draw segment
    const canvas = canvasRef.current;
    if (canvas && lastPointRef.current) {
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = sharedColor.toLowerCase();
      ctx.lineWidth = BRUSH_RADIUS * 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.globalAlpha = 0.45;
      ctx.beginPath();
      ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
      ctx.lineTo(pt.x, pt.y);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    lastPointRef.current = pt;

    // Check zone intersection
    const zone = findZoneForPoint(pt.x, pt.y);
    if (zone) {
      setColoredZones(prev => ({ ...prev, [zone.name]: sharedColor }));
    }
  };

  const handlePaintEnd = () => {
    isPaintingRef.current = false;
    lastPointRef.current = null;
  };

  // Global mouseup to end painting even if cursor leaves canvas
  useEffect(() => {
    window.addEventListener('mouseup', handlePaintEnd);
    window.addEventListener('touchend', handlePaintEnd);
    return () => {
      window.removeEventListener('mouseup', handlePaintEnd);
      window.removeEventListener('touchend', handlePaintEnd);
    };
  }, []);

  // Touch handlers
  const handleTouchStart = (e) => {
    if (isReviewMode || !sharedColor || !isHydrated) return;
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON') return;
    const touch = e.touches[0];
    if (!touch) return;
    handlePaintStart({ clientX: touch.clientX, clientY: touch.clientY, target: e.target, preventDefault: () => e.preventDefault() });
  };

  const handleTouchMove = (e) => {
    if (!isPaintingRef.current) return;
    const touch = e.touches[0];
    if (!touch) return;
    handlePaintMove({ clientX: touch.clientX, clientY: touch.clientY, preventDefault: () => e.preventDefault() });
  };

  const handleClearColors = () => {
    if (isReviewMode || !isHydrated) return;
    setColoredZones({});
    setPaintStrokes([]);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (e) => {
    if (isReviewMode || !isHydrated) return;
    e.preventDefault();
    if (e.dataTransfer.getData('type') !== 'inputBox') return;
    if (inputBoxes.length >= MAX_INPUT_BOXES) return;
    if (!imageContainerRef.current) return;

    const imageRect = imageContainerRef.current.getBoundingClientRect();
    const dropX = (e.clientX - imageRect.left) / zoomLevel;
    const dropY = (e.clientY - imageRect.top) / zoomLevel;

    // Clamp to image bounds
    const imageEl = imageContainerRef.current.querySelector('img');
    const maxX = imageEl ? imageEl.width : 9999;
    const maxY = imageEl ? imageEl.height : 9999;
    const x = Math.max(INPUT_BOX_W / 2, Math.min(dropX, maxX - INPUT_BOX_W / 2));
    const y = Math.max(INPUT_BOX_H / 2, Math.min(dropY, maxY - INPUT_BOX_H / 2));

    const matchedZone = findZoneForPoint(x, y);

    setInputBoxes(prev => [
      ...prev,
      {
        id: Date.now().toString(),
        x,
        y,
        typedValue: '',
        zoneName: matchedZone?.name || null,
      },
    ]);
  };

  const handleInputChange = (id, value) => {
    setInputBoxes(prev => prev.map(b => b.id === id ? { ...b, typedValue: value } : b));
  };

  const handleRemoveInputBox = (id) => {
    if (isReviewMode) return;
    setInputBoxes(prev => prev.filter(b => b.id !== id));
  };

  // --- HOOKS ---

  // 1. Hydration
  useEffect(() => {
    if (!attemptId || !exerciseId || !exerciseData?.questions) return;

    if (isReviewMode) {
      const colorState = {};
      const boxes = [];
      exerciseData.questions.forEach(q => {
        if (q.question_text?.toLowerCase().startsWith('input')) {
          // Always push input questions in review — even if no user_answer
          boxes.push({
            id: q.id,
            questionId: q.id,
            x: q.user_answer?.zone_position?.x ?? null,
            y: q.user_answer?.zone_position?.y ?? null,
            typedValue: q.user_answer?.typed_value || '',
            zoneName: q.user_answer?.answer || null,
            is_correct: q.is_correct ?? false,
            accepted_zone: q.accepted_answers?.[0] || null,
          });
        } else if (q.user_answer && typeof q.user_answer.answer === 'string') {
          colorState[q.user_answer.answer] = q.question_text;
        }
      });
      setColoredZones(colorState);
      if (boxes.length > 0) setInputBoxes(boxes);
      setIsHydrated(true);
      return;
    }

    const attemptKey = `attempt_${attemptId}`;
    try {
      const saved = localStorage.getItem(attemptKey);
      if (saved) {
        const allAnswers = JSON.parse(saved);
        const savedData = allAnswers[exerciseId];
        if (savedData) {
          const firstVal = Object.values(savedData)[0];
          if (firstVal && typeof firstVal === 'object' && 'answer' in firstVal) {
            const colorState = {};
            const boxes = [];
            Object.entries(savedData).forEach(([qId, item]) => {
              if (item.question_text?.toLowerCase().startsWith('input')) {
                boxes.push({
                  id: qId,
                  x: item.zone_position?.x ?? 80,
                  y: item.zone_position?.y ?? 80,
                  typedValue: item.typed_value || '',
                  zoneName: item.answer || null,
                });
              } else {
                colorState[item.answer] = item.question_text;
              }
            });
            setColoredZones(colorState);
            if (boxes.length > 0) setInputBoxes(boxes);
          } else {
            setColoredZones(savedData);
          }
        }
        // Set color example if empty
        if (Object.keys(coloredZones).length === 0) {
          const ex = exerciseData.questions.find(q => q.points === 0 && !q.question_text?.toLowerCase().startsWith('input'));
          if (ex?.accepted_answers?.[0]) {
            setColoredZones(prev => ({ ...prev, [ex.accepted_answers[0]]: ex.question_text }));
          }
        }
      }
    } catch (e) {
      console.error('Failed to parse saved attempt', e);
    }
    setIsHydrated(true);
  }, [attemptId, exerciseId, isReviewMode, exerciseData]);

  // 2. Save
  const saveAnswersToLocalStorage = useCallback((currentColoredZones, currentInputBoxes) => {
    if (!isHydrated || !attemptId || !exerciseId || !exerciseData?.questions) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const s = localStorage.getItem(attemptKey);
      if (s) allAnswers = JSON.parse(s) || {};
    } catch (e) { allAnswers = {}; }

    const answersToSave = {};

    // Color zones
    Object.entries(currentColoredZones).forEach(([zoneName, colorName]) => {
      const q = exerciseData.questions.find(q => q.question_text === colorName);
      if (q) answersToSave[q.id] = { answer: zoneName, question_text: colorName };
    });

    // Input boxes — map by index to inputQuestions
    currentInputBoxes.forEach((box, idx) => {
      const q = inputQuestions[idx];
      if (!q) return;
      answersToSave[q.id] = {
        answer: box.zoneName || '',
        question_text: q.question_text,
        typed_value: box.typedValue,
        zone_position: { x: box.x, y: box.y },
      };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, exerciseData, inputQuestions]);

  // 3. Save effect
  useEffect(() => {
    if (!isHydrated) return;
    const t = setTimeout(() => saveAnswersToLocalStorage(coloredZones, inputBoxes), 500);
    return () => clearTimeout(t);
  }, [coloredZones, inputBoxes, isHydrated, saveAnswersToLocalStorage]);

  // 4. Fill hint
  useEffect(() => {
    if (fillHintSignal > 0) {
      const newColoredZones = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers?.length > 0 && !q.question_text?.toLowerCase().startsWith('input')) {
          newColoredZones[q.accepted_answers[0]] = q.question_text;
        }
      });
      setColoredZones(newColoredZones);
      saveAnswersToLocalStorage(newColoredZones, inputBoxes);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage, inputBoxes]);

  // --- RENDER ---

  if (!exerciseData?.content_data) return <div>Loading exercise data...</div>;

  const { image_url, zones } = exerciseData.content_data;

  const canPaint = !isReviewMode && sharedColor && isHydrated;

  return (
    <div className="h-full w-full flex flex-col p-1 md:px-10">
      <div className="flex-1 w-full h-full flex justify-center items-center bg-slate-100 dark:bg-slate-800/50 rounded-3xl shadow-inner border-2 border-gray-200 dark:border-slate-700 overflow-hidden relative p-2">
        <div
          ref={imageContainerRef}
          className="relative inline-flex shadow-2xl rounded-2xl border-4 border-white dark:border-slate-600 bg-white"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          style={{ maxWidth: '100%', maxHeight: '100%', cursor: canPaint ? 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\'%3E%3Ccircle cx=\'12\' cy=\'12\' r=\'8\' fill=\'%23' + (sharedColor || '999').replace('#', '') + '\' fill-opacity=\'0.6\' stroke=\'%23333\' stroke-width=\'1\'/%3E%3C/svg%3E") 12 12, crosshair' : 'crosshair' }}
        >
          <img
            src={`${ASSET_BASE_URL}${image_url}`}
            alt="exercise illustration"
            onLoad={handleImageLoad}
            className="block w-auto h-auto max-w-full max-h-full rounded-xl object-contain"
            style={{ maxHeight: '80vh' }}
            draggable="false"
          />

          {/* Paint canvas overlay */}
          {isImageLoaded && !isReviewMode && (
            <canvas
              ref={canvasRef}
              className="absolute top-0 left-0 rounded-xl"
              style={{ pointerEvents: canPaint ? 'auto' : 'none' }}
              onMouseDown={handlePaintStart}
              onMouseMove={handlePaintMove}
              onMouseUp={handlePaintEnd}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handlePaintEnd}
            />
          )}

          {isImageLoaded && (
            <svg className="absolute top-0 left-0 w-full h-full pointer-events-none">
              {/* Zone outlines */}
              {zones.map(zone => {
                const { scaleX, scaleY } = getScale();
                return (
                  <rect
                    key={`zone-outline-${zone.name}`}
                    x={zone.rect.x1 / scaleX}
                    y={zone.rect.y1 / scaleY}
                    width={(zone.rect.x2 - zone.rect.x1) / scaleX}
                    height={(zone.rect.y2 - zone.rect.y1) / scaleY}
                    fill="none"
                    stroke="grey"
                    strokeWidth="1"
                    strokeDasharray="5,5"
                  />
                );
              })}

              {/* Missed color answers in review */}
              {isReviewMode && colorQuestions.map(q => {
                if (q.points === 0) return null;
                if (Object.values(coloredZones).includes(q.question_text)) return null;
                const correctZoneName = q.accepted_answers?.[0];
                const zone = zones.find(z => z.name === correctZoneName);
                if (!zone) return null;
                const { scaleX, scaleY } = getScale();
                const centerX = (zone.rect.x1 + zone.rect.x2) / (2 * scaleX);
                const centerY = (zone.rect.y1 + zone.rect.y2) / (2 * scaleY);
                return (
                  <g key={`missed-${q.id}`}>
                    <rect
                      x={zone.rect.x1 / scaleX} y={zone.rect.y1 / scaleY}
                      width={(zone.rect.x2 - zone.rect.x1) / scaleX}
                      height={(zone.rect.y2 - zone.rect.y1) / scaleY}
                      fill="none" stroke="#ef4444" strokeWidth="2" strokeDasharray="4,4"
                    />
                    <g transform={`translate(${centerX}, ${centerY})`}>
                      <circle r="12" fill="#fee2e2" stroke="#ef4444" strokeWidth="2" />
                      <text textAnchor="middle" dominantBaseline="central" fill="#dc2626" fontSize="18" fontWeight="bold"
                        className="material-symbols-outlined" style={{ fontFamily: "'Material Symbols Outlined'" }}>
                        close
                      </text>
                    </g>
                  </g>
                );
              })}

              {/* Colored zones — in review, show zone fills; in attempt, paint canvas shows strokes */}
              {isReviewMode && Object.entries(coloredZones).map(([zoneName, color]) => {
                const zone = zones.find(z => z.name === zoneName);
                if (!zone) return null;
                const { scaleX, scaleY } = getScale();
                const q = colorQuestions.find(q => q.question_text === color);
                const isCorrect = q?.accepted_answers?.[0] === zoneName;
                const cx = (zone.rect.x1 + zone.rect.x2) / (2 * scaleX);
                const cy = (zone.rect.y1 + zone.rect.y2) / (2 * scaleY);
                return (
                  <g key={zoneName}>
                    <rect
                      x={zone.rect.x1 / scaleX} y={zone.rect.y1 / scaleY}
                      width={(zone.rect.x2 - zone.rect.x1) / scaleX}
                      height={(zone.rect.y2 - zone.rect.y1) / scaleY}
                      fill={color.toLowerCase()} fillOpacity="0.5"
                      stroke="none"
                    />
                    <g transform={`translate(${cx}, ${cy})`}>
                      <circle r="12" fill={isCorrect ? "#dcfce7" : "#fee2e2"} stroke={isCorrect ? "#22c55e" : "#ef4444"} strokeWidth="2" />
                      <text textAnchor="middle" dominantBaseline="central" fill={isCorrect ? "#16a34a" : "#dc2626"}
                        fontSize="18" fontWeight="bold" className="material-symbols-outlined"
                        style={{ fontFamily: "'Material Symbols Outlined'" }}>
                        {isCorrect ? "check" : "close"}
                      </text>
                    </g>
                  </g>
                );
              })}

              {/* In attempt mode, show a subtle check on zones that have been colored */}
              {!isReviewMode && Object.entries(coloredZones).map(([zoneName, color]) => {
                const zone = zones.find(z => z.name === zoneName);
                if (!zone) return null;
                const { scaleX, scaleY } = getScale();
                const cx = zone.rect.x2 / scaleX - 14;
                const cy = zone.rect.y1 / scaleY + 14;
                return (
                  <g key={`painted-${zoneName}`}>
                    <circle cx={cx} cy={cy} r="10" fill={color.toLowerCase()} stroke="#fff" strokeWidth="2" opacity="0.8" />
                  </g>
                );
              })}

              {/* Input questions review: draw correct zone for wrong answers */}
              {isReviewMode && inputBoxes.map(box => {
                if (box.is_correct) return null;
                if (!box.accepted_zone) return null;
                const zone = zones.find(z => z.name === box.accepted_zone);
                if (!zone) return null;
                const { scaleX, scaleY } = getScale();
                const cx = (zone.rect.x1 + zone.rect.x2) / (2 * scaleX);
                const zoneW = (zone.rect.x2 - zone.rect.x1) / scaleX;
                const zoneH = (zone.rect.y2 - zone.rect.y1) / scaleY;
                return (
                  <g key={`input-correct-zone-${box.id}`}>
                    <rect
                      x={zone.rect.x1 / scaleX} y={zone.rect.y1 / scaleY}
                      width={zoneW} height={zoneH}
                      fill="rgba(34,197,94,0.15)" stroke="#22c55e" strokeWidth="2.5" strokeDasharray="6,3"
                    />
                    <text
                      x={cx} y={zone.rect.y1 / scaleY - 6}
                      textAnchor="middle" fill="#16a34a" fontSize="11" fontWeight="bold"
                      style={{ paintOrder: 'stroke', stroke: 'white', strokeWidth: '3px' }}
                    >
                      Correct
                    </text>
                  </g>
                );
              })}

              {/* Hint overlay */}
              {(showHint || isReviewMode) && colorQuestions.map(q => {
                if (isReviewMode && q.is_correct) return null;
                if (!q.accepted_answers?.length) return null;
                const zone = zones.find(z => z.name === q.accepted_answers[0]);
                if (!zone) return null;
                const { scaleX, scaleY } = getScale();
                return (
                  <g key={`hint-${zone.name}`}>
                    <rect
                      x={zone.rect.x1 / scaleX} y={zone.rect.y1 / scaleY}
                      width={(zone.rect.x2 - zone.rect.x1) / scaleX}
                      height={(zone.rect.y2 - zone.rect.y1) / scaleY}
                      fill={q.question_text.replace(/\(Example\)/gi, '').trim().toLowerCase()} fillOpacity="0.8"
                      stroke="#d97706" strokeWidth="3" strokeDasharray="5,5"
                    />
                    <text
                      x={zone.rect.x1 / scaleX + 5} y={zone.rect.y1 / scaleY + 20}
                      className="font-black" fill="#d97706"
                      style={{ paintOrder: 'stroke', stroke: 'white', strokeWidth: '2px' }}
                    >
                      <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                        {q.question_text.replace(/\(Example\)/gi, '').trim().toUpperCase()}
                      </span>
                    </text>
                  </g>
                );
              })}
            </svg>
          )}

          {/* Per-question Learn icons at the top-right corner of each zone (review only). */}
          {isReviewMode && isImageLoaded && (
            <div className="absolute top-0 left-0 w-full h-full" style={{ pointerEvents: 'none' }}>
              {(() => {
                const seen = new Set();
                return (exerciseData?.questions || []).flatMap((q) => {
                  if (q.points === 0) return [];
                  const zoneName = q.accepted_answers?.[0];
                  if (!zoneName || seen.has(zoneName)) return [];
                  seen.add(zoneName);
                  const zone = zones.find((z) => z.name === zoneName);
                  if (!zone) return [];
                  const { scaleX, scaleY } = getScale();
                  const left = (zone.rect.x2 / scaleX) - 26;
                  const top = (zone.rect.y1 / scaleY) + 4;
                  return [(
                    <div
                      key={`learn-icon-${q.id}`}
                      style={{ position: 'absolute', left, top, pointerEvents: 'auto' }}
                    >
                      <QuestionLearnButton question={q} />
                    </div>
                  )];
                });
              })()}
            </div>
          )}

          {/* Input box overlays — absolutely positioned at drop coordinates */}
          {isImageLoaded && inputBoxes.map((box) => {
            // In review mode, if no position recorded → place inside correct zone center
            let posX = box.x;
            let posY = box.y;
            if (isReviewMode && (posX === null || posY === null) && box.accepted_zone) {
              const zone = zones.find(z => z.name === box.accepted_zone);
              if (zone) {
                const { scaleX, scaleY } = getScale();
                posX = (zone.rect.x1 + zone.rect.x2) / (2 * scaleX);
                posY = (zone.rect.y1 + zone.rect.y2) / (2 * scaleY);
              }
            }
            if (posX === null || posY === null) return null;

            let borderStyle = '2px dashed #a855f7';
            let bgColor = 'rgba(243,232,255,0.92)';
            let icon = null;
            if (isReviewMode) {
              if (box.is_correct) {
                borderStyle = '2px solid #22c55e';
                bgColor = 'rgba(220,252,231,0.92)';
                icon = '✓';
              } else {
                borderStyle = '2px solid #ef4444';
                bgColor = 'rgba(254,226,226,0.92)';
                icon = '✗';
              }
            }

            return (
              <div
                key={box.id}
                style={{
                  position: 'absolute',
                  left: posX - INPUT_BOX_W / 2,
                  top: posY - INPUT_BOX_H / 2,
                  width: INPUT_BOX_W,
                  height: INPUT_BOX_H,
                  zIndex: 20,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {isReviewMode && icon && (
                  <span style={{
                    position: 'absolute',
                    left: -18,
                    fontSize: 14,
                    fontWeight: 'bold',
                    color: box.is_correct ? '#16a34a' : '#dc2626',
                  }}>{icon}</span>
                )}
                <input
                  type="text"
                  value={box.typedValue}
                  onChange={(e) => handleInputChange(box.id, e.target.value)}
                  disabled={isReviewMode}
                  placeholder="type word..."
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    flex: 1,
                    height: '100%',
                    background: bgColor,
                    border: borderStyle,
                    borderRadius: 6,
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: 13,
                    outline: 'none',
                    cursor: isReviewMode ? 'default' : 'text',
                    padding: '2px 4px',
                    minWidth: 0,
                  }}
                />
                {!isReviewMode && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRemoveInputBox(box.id); }}
                    style={{
                      position: 'absolute',
                      right: -8,
                      top: -8,
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                    }}
                  >×</button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ListenPart5;
